import { Link, useLocation, useNavigate } from "react-router-dom"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Trophy,
  Calendar,
  Store,
  User,
  LogOut,
  Settings,
  ChevronDown,
  Bell,
  StoreIcon,
  BarChart2,
  Moon,
  Sun,
} from "lucide-react"
import { useTheme } from "next-themes"
import { Skeleton } from "@/components/ui/skeleton"
import { useAuthContext } from "@/contexts/AuthContext"
import { useTournamentListHeader } from "@/graphql/hooks/useTournamentList"
import { Badge } from "@/components/ui/badge"
import { UserAvatar } from "../ui/user-avatar"
import { TournamentStatusBadge } from "../ui/TournamentStatusBadge"

const TROPHY_COLORS = [
  "#ef4444", // rojo
  "#a855f7", // morado
  "#3b82f6", // azul
  "#eab308", // dorado
]

const Header = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated, user, person, logout, loading, isAdmin } = useAuthContext()

  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  // Asegura que el tema se renderice correctamente en cliente
  useEffect(() => setMounted(true), [])

  const [trophyColorIdx, setTrophyColorIdx] = useState(0)
  useEffect(() => {
    const id = setInterval(() => {
      setTrophyColorIdx((prev) => (prev + 1) % TROPHY_COLORS.length)
    }, 2000)
    return () => clearInterval(id)
  }, [])

  // Get user's active tournaments for notifications
  const { tournaments: userTournaments, loading: tournamentsLoading } = useTournamentListHeader(
    {
      input: {
        gameId: 0, // Required parameter, use 0 for all games
        page: 1,
        limit: 5,
        players: [person?.id],
        date: "DESC",
        statusId: [2, 3, 4, 5], // Active and In Progress tournaments
      },
    },
    isAuthenticated,
  )

  const isActive = (path: string) => {
    const current = location.pathname.split("/").filter(Boolean)
    const target = path.split("/").filter(Boolean)

    return target.every((segment, i) => current[i] === segment)
  }

  const navigation = [
    { name: "Ranking", path: "/ranking", icon: Trophy },
    { name: "Eventos", path: "/tournaments", icon: Calendar },
    { name: "Tiendas", path: "/stores", icon: Store },
  ]

  const getUserDisplayName = () => {
    if (person?.fullName) {
      return person.fullName
    }
    if (person?.name && person?.firstSurname) {
      return `${person.name} ${person.firstSurname}`
    }
    if (user?.username) {
      return user.username
    }
    return "Usuario"
  }

  const getUserAvatar = () => {
    return person?.urlAvatar || person?.avatar || user?.avatar
  }

  const colorMap = {
    "Furia Extendido": "255, 0, 0", // rojo
    "Primer Bloque": "230, 200, 40", // dorado menos brillante
    "Primera Era": "128, 0, 255", // morado
    Imperio: "0, 120, 200", // azul medio
  }

  const gradientMap = {
    "Furia Extendido": `linear-gradient(135deg, rgb(${colorMap["Furia Extendido"]}), rgb(255,120,120))`,
    "Primer Bloque": `linear-gradient(135deg, rgb(${colorMap["Primer Bloque"]}), rgb(200,160,20))`,
    "Primera Era": `linear-gradient(135deg, rgb(${colorMap["Primera Era"]}), rgb(180,100,255))`,
    Imperio: `linear-gradient(135deg, rgb(${colorMap["Imperio"]}), rgb(100,200,255))`,
  }

  return (
    <header className="border-b border-border bg-card/95 backdrop-blur-md sticky top-0 z-50 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3">
            <div
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shadow-glow transition-colors duration-700 hidden md:flex"
              style={{ backgroundColor: TROPHY_COLORS[trophyColorIdx] }}
            >
              <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="hidden sm:block">
              {/* Logo normal (modo claro) */}
              <img
                src="/img/logo/Torneos-MyL-Logo-Osc.png"
                alt="Torneos MyL"
                className="h-8 dark:hidden"
              />
              {/* Logo para dark mode */}
              <img
                src="/img/logo/Torneos-MyL-Logo-Blanco.png"
                alt="Torneos MyL"
                className="h-8 hidden dark:block"
              />
            </div>
            <span className="text-sm font-bold bg-gradient-primary bg-clip-text text-transparent sm:hidden">
              MyL
            </span>
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-4 lg:space-x-6">
            {navigation.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300 ${
                    isActive(item.path)
                      ? "bg-gradient-primary text-white shadow-glow"
                      : "text-muted-foreground hover:text-primary hover:bg-muted"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium text-sm lg:text-base">{item.name}</span>
                </Link>
              )
            })}
          </nav>

          {/* Mobile Navigation */}
          <nav className="md:hidden flex items-center space-x-1">
            {navigation.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center p-2 rounded-lg transition-all duration-300 ${
                    isActive(item.path)
                      ? "bg-gradient-primary text-white shadow-glow"
                      : "text-muted-foreground hover:text-primary hover:bg-muted"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                </Link>
              )
            })}
          </nav>

          {/* Auth Section */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Notifications - only show when authenticated */}
            {isAuthenticated && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="relative p-2">
                    <Bell className="w-5 h-5 text-muted-foreground" />
                    {!tournamentsLoading && userTournaments && userTournaments.length > 0 && (
                      <Badge
                        variant="destructive"
                        className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
                      >
                        {userTournaments.length}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-[min(30rem,calc(100vw-1rem))] sm:w-80 md:w-96"
                >
                  <div className="px-4 py-3 border-b">
                    {userTournaments && userTournaments.length > 0 ? (
                      <div className="flex items-center space-x-2">
                        <Trophy className="w-5 h-5 text-primary" />
                        <span className="text-sm font-medium">
                          Tienes {userTournaments.length} torneo
                          {userTournaments.length > 1 ? "s" : ""} activo
                          {userTournaments.length > 1 ? "s" : ""}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Trophy className="w-5 h-5 text-muted-foreground" />
                        <span className="text-sm font-medium text-muted-foreground">
                          No tienes torneos recientes. Inscríbete para recibir notificaciones.
                        </span>
                      </div>
                    )}
                  </div>
                  {tournamentsLoading ? (
                    <div className="p-4 sm:p-3">
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  ) : userTournaments && userTournaments.length > 0 ? (
                    <div className="max-h-80 overflow-y-auto">
                      {userTournaments.map((tournament, key) => (
                        <DropdownMenuItem
                          key={key}
                          className="cursor-pointer p-4 sm:p-3 flex flex-col items-start"
                          onClick={() => navigate(tournament.urlView)}
                        >
                          <div className="flex w-full items-start space-x-3 sm:space-x-2">
                            {/* Ícono con gradiente */}
                            <div
                              className="flex h-10 w-10 shrink-0 items-center justify-center rounded sm:h-8 sm:w-8"
                              style={{ background: gradientMap[tournament.Game?.name] }}
                            >
                              <Trophy className="h-5 w-5 text-white sm:h-4 sm:w-4" />
                            </div>

                            {/* Info del torneo */}
                            <div className="min-w-0 flex-1">
                              {/* Nombre del torneo */}
                              <p className="truncate text-sm font-small ">{tournament.name}</p>

                              {/* Subinfo: juego + tienda */}
                              <div className="mt-0.5 flex flex-col sm:flex-row sm:items-center sm:justify-between sm:space-x-2">
                                <span className="bg-gradient-to-r from-primary via-primary-glow to-primary bg-clip-text text-[11px] font-semibold text-transparent sm:text-xs bg-[length:200%_200%] animate-gradient">
                                  {tournament.Game?.name}
                                </span>
                                <span className="text-[11px] text-muted-foreground sm:text-xs flex items-center mt-0.5 sm:mt-0">
                                  <StoreIcon className="mr-1 h-3 w-3 shrink-0" />
                                  <span className="truncate">{tournament.Store?.name}</span>
                                </span>
                              </div>
                            </div>

                            {/* Badge de estado */}
                            <TournamentStatusBadge
                              statusId={tournament.statusId}
                              className="ml-2 h-3 sm:h-2 shrink-0"
                              mini
                            />
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 sm:p-3 text-center text-muted-foreground text-base sm:text-sm">
                      Inscríbete en torneos para recibir notificaciones aquí.
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {loading ? (
              <div className="flex items-center space-x-2">
                <Skeleton className="w-9 h-9 rounded-full" />
                <div className="hidden sm:flex flex-col space-y-1">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
            ) : isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center space-x-2 px-2 py-2 h-auto">
                    <UserAvatar
                      src={getUserAvatar()}
                      name={getUserDisplayName()}
                      size="sm"
                      className="w-8 h-8 sm:w-9 sm:h-9"
                    />
                    <div className="hidden sm:flex flex-col items-start">
                      <span className="text-sm font-medium text-foreground">
                        {getUserDisplayName()}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Código: {person?.code || "???"}
                      </span>
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground hidden sm:block" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-full">
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() =>
                      location.pathname === "/profile/stats" ||
                      location.pathname === "/admin/player-stats"
                        ? (window.location.href = "/profile")
                        : navigate("/profile")
                    }
                  >
                    <User className="w-4 h-4 mr-2" />
                    Mi Perfil
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() =>
                      location.pathname === "/profile/stats" ||
                      location.pathname === "/admin/player-stats"
                        ? (window.location.href = "/profile/tournaments")
                        : navigate("/profile/tournaments")
                    }
                  >
                    <Trophy className="w-4 h-4 mr-2" />
                    Mis Torneos
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate("/profile/stats")}
                  >
                    <BarChart2 className="w-4 h-4 mr-2" />
                    Mis Estadísticas
                  </DropdownMenuItem>
                  {person?.Store && (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() =>
                        location.pathname === "/profile/stats" ||
                        location.pathname === "/admin/player-stats"
                          ? (window.location.href = person.Store.urlView)
                          : navigate(person.Store.urlView)
                      }
                    >
                      <Store className="w-4 h-4 mr-2" />
                      Tienda: {person.Store.name}
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <>
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() =>
                          location.pathname === "/profile/stats" ||
                          location.pathname === "/admin/player-stats"
                            ? (window.location.href = "/admin")
                            : navigate("/admin")
                        }
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Configuraciones
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() =>
                          location.pathname === "/profile/stats" ||
                          location.pathname === "/admin/player-stats"
                            ? (window.location.href = "/admin/users")
                            : navigate("/admin/users")
                        }
                      >
                        <User className="w-4 h-4 mr-2" />
                        Gestión de usuarios
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  {mounted && (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onSelect={(e) => {
                        e.preventDefault()
                        setTheme(theme === "dark" ? "light" : "dark")
                      }}
                    >
                      {theme === "dark" ? (
                        <Sun className="w-4 h-4 mr-2 text-yellow-400" />
                      ) : (
                        <Moon className="w-4 h-4 mr-2" />
                      )}
                      {theme === "dark" ? "Modo claro" : "Modo oscuro"}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="cursor-pointer text-destructive focus:text-destructive"
                    onClick={logout}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Link to="/login">
                  <Button variant="alternative" size="sm" className="text-sm">
                    <span className="hidden sm:inline">Iniciar Sesión</span>
                    <span className="sm:hidden">Ingresa</span>
                  </Button>
                </Link>
                <Link to="/register">
                  <Button
                    variant="default"
                    size="sm"
                    className="bg-gradient-primary shadow-glow text-sm"
                  >
                    <span className="hidden sm:inline">Regístrate</span>
                    <span className="sm:hidden">Registro</span>
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
